import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import {
  GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
  GET_COCOPLUS_RECOMMENDED_USERS,
  GET_HIGH_REVIEW_SCORE_USERS,
  GET_USERS_BY_PREFERENCE,
  GET_USERS_BY_PREFERENCE_EXCLUDING_LONG_CALL,
  MEET_PEOPLE_VERSION_2,
  UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
} from "~/constants/apiEndpoints";
import { apiClient } from "~/lib/apiClient";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { queryKeys } from "~/lib/queryKeys";
import type {
  CocoplusRecommendedUsersResponse,
  HighReviewScoreUsersResponse,
  PreferenceMatchUsersResponse,
  RankedDiscoverUser,
  RecommendRankingResponse,
} from "~/types/discoverSectionUser";
import type { DiscoverUserListResponse } from "~/types/discoverUser";

// さがす画面とおすすめ画面で共有する、ユーザー一覧セクションの取得定義

const HIGH_REVIEW_SCORE_LIMIT = 20;

// API によって last_login_time / last_login どちらで返るか不定なため正規化
export const fetchHighReviewScoreUsers = async (
  limit: number,
  offset?: number,
) => {
  const users = await apiClient<HighReviewScoreUsersResponse>(
    GET_HIGH_REVIEW_SCORE_USERS,
    offset === undefined ? { limit } : { limit, offset },
  );
  return users.map((u) => ({
    ...u,
    last_login_time: u.last_login_time || u.last_login || "",
  }));
};

export const highReviewScoreUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.highReviewScoreUsers(),
    queryFn: () => fetchHighReviewScoreUsers(HIGH_REVIEW_SCORE_LIMIT),
    // サーバー側が1時間キャッシュするため、クライアント側も同じ時間を設定
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

const COCOPLUS_HIGH_REVIEW_SCORE_LIMIT = 100;

// coco+ の「評価が高い女の子」タブ用。
// API はカーソル非対応のため多めにまとめて取得し画面側で分割表示する。
// BE がカーソル対応したら infiniteQueryOptions に差し替える。
export const cocoplusHighReviewScoreUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.highReviewScoreUsersWithLimit(
      COCOPLUS_HIGH_REVIEW_SCORE_LIMIT,
    ),
    queryFn: () => fetchHighReviewScoreUsers(COCOPLUS_HIGH_REVIEW_SCORE_LIMIT),
    // サーバー側が1時間キャッシュするため、クライアント側も同じ時間を設定
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  });

// coco+ のおすすめ枠（A/B の B 群のみ呼ぶ）。件数（3〜5）と対象（登録7日以内の新規など）はサーバーが決め、
// 対象外・A 群には空配列が返る。limit / offset は送らない。
export const cocoplusRecommendedUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.cocoplusRecommendedUsers(),
    queryFn: async () => {
      const users = await apiClient<CocoplusRecommendedUsersResponse>(
        GET_COCOPLUS_RECOMMENDED_USERS,
        // サーバーは coco+（145）以外には空を返すため、定数の APPLICATION_ID ではなく実際のアプリIDを送る
        { application_id: getCurrentApplicationId() },
      );
      // API によって last_login_time / last_login どちらで返るか不定なため正規化
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    },
    // さがす画面を開くたびに 1 回取れればよい。フォーカス復帰等では取り直さない
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

const RECENT_LOGIN_PAGE_SIZE = 20;

/** 「最近ログインした順」のページ位置。skip は表示ログの通し順位に使う */
type RecentLoginPageParam = { cursor?: string; skip: number };

// おすすめ画面の「最近ログインした順」。さがす一覧と同じエンドポイント・並び（sort_type: 0 = 最終ログインが新しい順）で、
// last_login_time をカーソルにして続きを取る。ビデオ通話チャンネラー除外で 1 ページが少なく返ることがあるため、終端はカーソルの有無で判定する
export const recentLoginUsersInfiniteQueryOptions = () =>
  infiniteQueryOptions({
    queryKey: queryKeys.discover.recentLoginUsers(),
    queryFn: ({ pageParam }) =>
      apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        {
          limit: RECENT_LOGIN_PAGE_SIZE,
          sort_type: 0,
          default_avatar_flag: false,
          skip: pageParam.skip,
          ...(pageParam.cursor !== undefined
            ? { last_login_time: pageParam.cursor }
            : {}),
        },
      ),
    initialPageParam: { skip: 0 } as RecentLoginPageParam,
    getNextPageParam: (lastPage, allPages) => {
      const cursor = lastPage.at(-1)?.last_login_time;
      return cursor === undefined
        ? undefined
        : { cursor, skip: allPages.reduce((n, page) => n + page.length, 0) };
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

const NEWCOMER_LIMIT = 50;

// 新人タブ用。検索画面と同じエンドポイント・同じ is_new フィルタで新人のみ取得する
// （検索の buildSearchRequestBody が isNew=true のとき body.is_new=true を送るのと同等）。
export const newcomerUsersQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.newcomers(),
    queryFn: () =>
      apiClient<DiscoverUserListResponse>(
        UTAGE_WEB_GET_MEET_PEOPLE_EXCLUDE_VIDEO_CALL_CHANNELER,
        {
          limit: NEWCOMER_LIMIT,
          sort_type: 0,
          default_avatar_flag: false,
          is_new: true,
        },
      ),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// ビデオ通話ランキングタブ用。本家 Utage と同じ人を出すため
// get_category_ranking_for_utage_web を使う。type:2=ビデオ通話（0=総合 / 1=ツーショット / 3=メッセージ）。
export const RECOMMEND_RANKING_TYPE = 2;
const RECOMMEND_RANKING_TAKE = 50;

// ランキングのネストしたレスポンスを一覧カードが扱う DiscoverUser（順位付き）へフラット化する
export const toRankedDiscoverUser = (
  item: RecommendRankingResponse[number],
): RankedDiscoverUser => ({
  user_id: item.user_id,
  ava_id: item.user.ava_id || null,
  abt: item.user.abt ?? "",
  age: item.user.age,
  user_name: item.user.user_name,
  region: item.user.region,
  voice_call_waiting: item.user.voice_call_waiting,
  video_call_waiting: item.user.video_call_waiting,
  is_new_user: item.user.is_new,
  is_calling: false,
  is_live_now: false,
  last_login_time: item.user.last_login_time,
  has_story_movie: false,
  step_to_call: 0,
  has_lovense: item.has_lovense,
  is_listed_on_flea_market: item.user.is_listed_on_flea_market ?? false,
  average_score: item.average_score ?? undefined,
  review_count: item.review_count,
  rank: item.rank,
});

export const recommendRankingQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.discover.recommendRanking(),
    queryFn: async () => {
      const data = await apiClient<RecommendRankingResponse>(
        GET_CATEGORY_RANKING_FOR_UTAGE_WEB,
        {
          type: RECOMMEND_RANKING_TYPE,
          skip: 0,
          take: RECOMMEND_RANKING_TAKE,
        },
      );
      return data.map(toRankedDiscoverUser);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

// 「あなたの好みに合う子」セクション。オンボーディング（search_users_by_preference）で
// 保存済みの好み条件を使い、書き込みなしで検索だけ行う（docs/api/discover.md 参照）。
// 好み未保存のユーザーには絞り込みなし（通常一覧相当）が返る。
//
// excludeLongCall: 秋のポイントバック開催中は、3分以上通話した女性を除いた版を使う
// （通話済みの子は、もうキャンペーンの対象ではないため、一覧に出さない）。キャッシュキーも分ける
export const preferenceMatchUsersQueryOptions = (excludeLongCall = false) =>
  queryOptions({
    queryKey: excludeLongCall
      ? queryKeys.discover.preferenceMatchUsersExcludingLongCall()
      : queryKeys.discover.preferenceMatchUsers(),
    queryFn: async () => {
      const users = await apiClient<PreferenceMatchUsersResponse>(
        excludeLongCall
          ? GET_USERS_BY_PREFERENCE_EXCLUDING_LONG_CALL
          : GET_USERS_BY_PREFERENCE,
        {},
      );
      // このAPIは last_login_time ではなく last_login で返るため正規化する
      // （未正規化だとオンライン判定が常に offline= 灰色になる）
      return users.map((u) => ({
        ...u,
        last_login_time: u.last_login_time || u.last_login || "",
      }));
    },
  });

/** S/M な性格の女の子セクション。値はサーバーの personalities（4=Sっぽい / 5=Mっぽい） */
export const PERSONALITY_SECTION_KINDS = ["sadistic", "masochistic"] as const;
export type PersonalitySectionKind = (typeof PERSONALITY_SECTION_KINDS)[number];

export const PERSONALITY_VALUE: Record<PersonalitySectionKind, number> = {
  sadistic: 4,
  masochistic: 5,
};

/** おすすめ画面に出す件数 */
const PERSONALITY_PREVIEW_COUNT = 20;

/**
 * 性格で絞り込んだ女の子一覧。性格での絞り込みは meet_people_version_2 だけが対応している
 * （utage_web_get_meet_people_exclude_video_call_channeler は personalities を読まない）。
 * 必須項目は Kanade の一覧リクエストに合わせる。show_me は表示する性別（0=男性 / 1=女性）。
 */
export const fetchPersonalityUsers = async (
  kind: PersonalitySectionKind,
  skip: number,
  take: number,
) => {
  const users = await apiClient<PreferenceMatchUsersResponse>(
    MEET_PEOPLE_VERSION_2,
    {
      show_me: [1],
      lower_age: 18,
      upper_age: 100,
      over_fifty_age: true,
      is_new_login: false,
      filter: 0,
      distance: 4,
      skip,
      take,
      personalities: PERSONALITY_VALUE[kind],
    },
  );
  // このAPIは last_login_time ではなく last_login で返るため正規化する
  // （未正規化だとオンライン判定が常に offline= 灰色になる）
  return users.map((u) => ({
    ...u,
    last_login_time: u.last_login_time || u.last_login || "",
  }));
};

export const personalityUsersQueryOptions = (kind: PersonalitySectionKind) =>
  queryOptions({
    queryKey: queryKeys.discover.personalityUsers(kind),
    queryFn: () => fetchPersonalityUsers(kind, 0, PERSONALITY_PREVIEW_COUNT),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
